import {
    useId,
    type ButtonHTMLAttributes,
    type HTMLAttributes,
    type InputHTMLAttributes,
    type ReactNode,
    type SelectHTMLAttributes,
    type TextareaHTMLAttributes,
} from 'react';

/** One primary action per view; `default` for the others, `light` inside rows, `ghost` for icon buttons. */
const buttonVariants = {
    primary: 'border-transparent bg-brand text-on-brand hover:bg-brand-hover',
    default: 'border-line-strong bg-surface text-ink hover:bg-surface-muted',
    light: 'border-transparent bg-surface-muted text-ink hover:bg-line',
    ghost: 'border-transparent bg-transparent text-muted hover:bg-surface-muted hover:text-ink',
    danger: 'border-transparent bg-danger text-surface hover:opacity-90',
};
const buttonSizes = { xs: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm', icon: 'size-9 p-0' };
type ButtonStyle = { variant?: keyof typeof buttonVariants; size?: keyof typeof buttonSizes; className?: string };

/** Shared by buttons and router links, retaining their native semantics. */
export function buttonStyles({ variant = 'primary', size = 'md', className = '' }: ButtonStyle = {}) {
    return `inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonSizes[size]} ${buttonVariants[variant]} ${className}`;
}

/** Inline text links (external documents, secondary navigation). */
export const linkStyles =
    'font-semibold text-accent underline decoration-accent/40 underline-offset-2 transition-colors hover:decoration-accent';

export function Button({
    loading = false,
    disabled,
    type = 'button',
    variant,
    size,
    className,
    children,
    ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & ButtonStyle & { loading?: boolean }) {
    return (
        <button
            {...props}
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonStyles({ variant, size, className })}
        >
            {loading && (
                <span
                    aria-hidden="true"
                    className="size-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
                />
            )}
            {children}
        </button>
    );
}

const cardBaseStyles = 'rounded-xl border border-line bg-surface shadow-xs';
const cardPadding = { md: 'p-5', lg: 'p-8' };
export const cardStyles = `${cardBaseStyles} ${cardPadding.md}`;
export function Card({
    className = '',
    padding = 'md',
    ...props
}: HTMLAttributes<HTMLDivElement> & { padding?: 'md' | 'lg' }) {
    return <div {...props} className={`${cardBaseStyles} ${cardPadding[padding]} ${className}`} />;
}

export function Spinner({ label }: { label: string }) {
    return (
        <span role="status" className="inline-flex items-center gap-2">
            <span
                aria-hidden="true"
                className="size-7 animate-spin rounded-full border-3 border-line border-t-accent motion-reduce:animate-none"
            />
            <span className="sr-only">{label}</span>
        </span>
    );
}

/** Status tones, shared by badges and notices. Never convey a status by color alone: the text carries it. */
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';
const badgeTones: Record<Tone, string> = {
    neutral: 'bg-surface-muted text-muted',
    info: 'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
};
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
    return (
        <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeTones[tone]}`}>
            {children}
        </span>
    );
}

const noticeTones: Record<Tone, string> = {
    neutral: 'border-line bg-surface-muted text-ink',
    info: 'border-accent/30 bg-accent-soft text-ink',
    success: 'border-success/30 bg-success-soft text-ink',
    warning: 'border-warning/30 bg-warning-soft text-ink',
    danger: 'border-danger/30 bg-danger-soft text-danger',
};
/** A message about the whole view or form. `danger` is announced as an alert, the other tones as status. */
export function Notice({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
    return (
        <div
            role={tone === 'danger' ? 'alert' : 'status'}
            className={`rounded-lg border p-4 text-sm ${noticeTones[tone]}`}
        >
            {children}
        </div>
    );
}

/** Validation or failure text next to the control it concerns. */
export function InlineError({ children }: { children: ReactNode }) {
    return (
        <p role="alert" className="text-sm text-danger">
            {children}
        </p>
    );
}

const inputStyles =
    'w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-70 aria-invalid:border-danger file:mr-3 file:rounded-md file:border-0 file:bg-surface-muted file:px-3 file:py-1 file:text-sm file:font-semibold file:text-ink';
type FieldProps = { label: string; description?: string; error?: string };
export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & FieldProps;

function Field({
    id,
    label,
    description,
    error,
    required,
    children,
}: FieldProps & { id: string; required?: boolean; children: ReactNode }) {
    return (
        <div className="space-y-1.5">
            <label htmlFor={id} className="block text-sm font-semibold">
                {label}
                {required && (
                    <span aria-hidden="true" className="ml-1 text-danger">
                        *
                    </span>
                )}
            </label>
            {description && (
                <p id={`${id}-description`} className="text-sm text-muted">
                    {description}
                </p>
            )}
            {children}
            {error && (
                <p id={`${id}-error`} role="alert" className="text-sm text-danger">
                    {error}
                </p>
            )}
        </div>
    );
}

function useFieldAttributes(
    suppliedId: string | undefined,
    description: string | undefined,
    error: string | undefined,
    describedBy: string | undefined,
) {
    const generatedId = useId();
    const id = suppliedId ?? generatedId;
    return {
        id,
        'aria-invalid': Boolean(error) || undefined,
        'aria-describedby':
            [describedBy, description && `${id}-description`, error && `${id}-error`].filter(Boolean).join(' ') ||
            undefined,
    };
}

export function TextInput({
    id: suppliedId,
    label,
    description,
    error,
    required,
    className = '',
    'aria-describedby': describedBy,
    ...props
}: TextInputProps) {
    const attributes = useFieldAttributes(suppliedId, description, error, describedBy);
    return (
        <Field id={attributes.id} label={label} description={description} error={error} required={required}>
            <input {...props} {...attributes} required={required} className={`${inputStyles} ${className}`} />
        </Field>
    );
}

export function SelectInput({
    id: suppliedId,
    label,
    description,
    error,
    required,
    className = '',
    'aria-describedby': describedBy,
    ...props
}: SelectHTMLAttributes<HTMLSelectElement> & FieldProps) {
    const attributes = useFieldAttributes(suppliedId, description, error, describedBy);
    return (
        <Field id={attributes.id} label={label} description={description} error={error} required={required}>
            <select {...props} {...attributes} required={required} className={`${inputStyles} ${className}`} />
        </Field>
    );
}

export function Switch({
    label,
    className = '',
    ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string }) {
    return (
        <label className={`inline-flex items-center gap-3 text-sm ${className}`}>
            <input {...props} type="checkbox" role="switch" className="peer sr-only" />
            <span
                aria-hidden="true"
                className="relative h-6 w-11 shrink-0 rounded-full bg-line-strong transition-colors after:absolute after:top-1 after:left-1 after:size-4 after:rounded-full after:bg-surface after:shadow-xs after:transition-transform peer-checked:bg-accent peer-checked:after:translate-x-5 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent peer-disabled:opacity-50"
            />
            <span>{label}</span>
        </label>
    );
}

export function Checkbox({
    label,
    className = '',
    ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
    return (
        <label className={`flex items-start gap-2.5 text-sm ${className}`}>
            <input {...props} type="checkbox" className="mt-0.5 size-4 shrink-0 accent-accent" />
            <span>{label}</span>
        </label>
    );
}

export function TextArea({
    id: suppliedId,
    label,
    description,
    error,
    required,
    className = '',
    'aria-describedby': describedBy,
    ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldProps) {
    const attributes = useFieldAttributes(suppliedId, description, error, describedBy);
    return (
        <Field id={attributes.id} label={label} description={description} error={error} required={required}>
            <textarea {...props} {...attributes} required={required} className={`${inputStyles} ${className}`} />
        </Field>
    );
}
